import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import {
  LockClosedIcon,
  CalendarIcon,
  GlobeAmericasIcon,
  UsersIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import LazyLoad from "@tripbuilder/image/LazyLoad";
import { formatImageUrl } from "@/lib/general";
import { useToast } from "@/hooks/use-toast";
import { AvatarImage } from "@/components/ui/DefaultAvatar";
import PageHeaderBar from "@/components/common/PageHeaderBar";
import useAuthStore from "@/stores/user/authStore";
import tripService from "@/services/tripService";
import TripFilters from "./tripFilters";
import { TripX } from "@/types/trip";
import {
  Compass,
  Telescope,
  MapPinIcon,
  TrashIcon,
  PlusCircleIcon,
  Search,
} from "lucide-react";
import { CancelButton, ConfirmButton } from "@/components/common/buttons";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface TripCardProps {
  trip: TripX;
  isSelectionMode?: boolean;
  selectedTrips: string[];
  onSelectTrip: (tripId: string) => void;
}

const TripCard: React.FC<TripCardProps> = ({
  trip,
  isSelectionMode,
  selectedTrips,
  onSelectTrip,
}) => {
  const navigate = useNavigate();
  const firstLocation = trip.locations[0];
  const lastLocation = trip.locations[trip.locations.length - 1];
  const colours = ["indigo", "blue", "green", "yellow", "red"];
  const randomColour = colours[Math.floor(Math.random() * colours.length)];

  const handleCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isSelectionMode) {
      onSelectTrip(trip.id);
    } else {
      navigate(`/trip-builder/${trip.id}`);
    }
  };

  return (
    <div
      onClick={handleCardClick}
      className={`group w-full 2xl:w-[95%] rounded-xl overflow-hidden flex-shrink-0 hover:shadow-sm cursor-pointer transition-all duration-300 bg-white border border-slate-200 hover:border-slate-300 relative`}
    >
      {isSelectionMode && (
        <div className="absolute inset-0 z-10 transition-all duration-300 bg-white/60">
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                selectedTrips.includes(trip.id)
                  ? "bg-indigo-600 scale-100 border-4 border-indigo-100"
                  : "bg-white/90 border-4 border-indigo-200 scale-90"
              }`}
            >
              <CheckIcon
                className={`w-5 h-5 transition-all duration-300 ${
                  selectedTrips.includes(trip.id)
                    ? "text-white scale-100 opacity-100"
                    : "text-neutral-200 scale-75 opacity-0"
                }`}
                strokeWidth={3}
              />
            </div>
          </div>
        </div>
      )}

      <div className="relative">
        <div className="w-full h-48 2xl:h-52 overflow-hidden">
          {/* Privacy Badge */}
          <div className="absolute top-2 left-2 z-10">
            <div className="bg-white/80 backdrop-blur-sm px-2.5 py-1 rounded-full text-xs 2xl:text-sm font-normal shadow-sm flex items-center space-x-1.5">
              {trip.privacy_status === "private" ? (
                <LockClosedIcon className="w-4 h-4 2xl:w-4 2xl:h-4 text-slate-600" />
              ) : trip.privacy_status === "friends" ? (
                <UsersIcon className="w-4 h-4 2xl:w-4 2xl:h-4 text-slate-600" />
              ) : (
                <GlobeAmericasIcon className="w-4 h-4 2xl:w-4 2xl:h-4 text-slate-600" />
              )}
              <span className="capitalize text-slate-700">
                {trip.privacy_status}
              </span>
            </div>
          </div>

          {/* Location Badge */}
          {trip.locations.filter((loc) => !loc.origin).length > 0 && (
            <div className="absolute top-2 right-2 z-10">
              <div className="bg-white/80 backdrop-blur-sm text-slate-700 px-2 py-1 rounded-full text-xs 2xl:text-sm font-normal shadow-sm flex items-center space-x-1.5">
                <MapPinIcon
                  className="w-4 h-4 2xl:w-4 2xl:h-4 text-orange-600"
                  strokeWidth={1.5}
                />
                <span>
                  {trip.locations.filter((loc) => !loc.origin).length}{" "}
                  {trip.locations.filter((loc) => !loc.origin).length === 1
                    ? "Location"
                    : "Locations"}
                </span>
              </div>
            </div>
          )}

          <div className="w-full h-full">
            {trip.images?.[0] ? (
              <LazyLoad
                src={formatImageUrl(trip.images[0], "high")}
                lowResSrc={formatImageUrl(trip.images[0], "low")}
                alt={trip.name}
                className="w-full h-full object-cover transition duration-700 ease-in-out group-hover:scale-105"
                objectFit="cover"
              />
            ) : (
              <div
                className={`w-full h-full bg-gradient-to-br from-${randomColour}-400 to-${randomColour}-600 flex items-center justify-center`}
              >
                <span className="text-white text-3xl 2xl:text-3xl font-semibold">
                  {trip.name?.[0]?.toUpperCase() || "T"}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="py-2 px-3 2xl:py-2.5 2xl:px-3.5">
        <div className="flex justify-between items-start">
          <h3 className="font-semibold text-base 2xl:text-base text-slate-800 line-clamp-1 flex-1">
            {trip.name}
          </h3>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 text-xs 2xl:text-sm text-neutral-700">
            <CalendarIcon className="w-4 h-4 2xl:w-5 2xl:h-5 text-neutral-700 flex-shrink-0" />
            <span className="truncate">
              {firstLocation?.date.start_date &&
                format(new Date(firstLocation.date.start_date), "MMM d")}{" "}
              -{" "}
              {lastLocation?.date.end_date &&
                format(new Date(lastLocation.date.end_date), "MMM d")}
            </span>
          </div>

          <div className="flex items-center space-x-3">
            <div className="flex -space-x-2">
              {trip.participants.slice(0, 3).map((participant) => (
                <div
                  key={participant.user_id}
                  className="w-6 h-6 2xl:w-8 2xl:h-8 rounded-full border-2 border-white"
                  title={participant.username || "Unknown user"}
                >
                  <AvatarImage
                    src={participant.profile_pic}
                    alt={participant.username || "User"}
                    firstName={participant.first_name || ""}
                    lastName={participant.last_name || ""}
                    className="w-full h-full"
                  />
                </div>
              ))}
              {trip.participants.length > 3 && (
                <div className="w-7 h-7 2xl:w-8 2xl:h-8 rounded-full border-2 border-white bg-slate-100 flex items-center justify-center">
                  <span className="text-xs 2xl:text-sm font-medium text-slate-600">
                    +{trip.participants.length - 3}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface DeleteConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  selectedCount: number;
  isDeleting: boolean;
}

const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  selectedCount,
  isDeleting,
}) => (
  <Dialog open={isOpen} onOpenChange={onClose}>
    <DialogContent className="rounded-2xl sm:max-w-[500px]">
      <DialogHeader className="flex flex-row items-start space-x-4 space-y-0">
        <div className="w-10 h-10 bg-rose-100 rounded-full flex items-center justify-center flex-shrink-0">
          <TrashIcon className="w-5 h-5 text-rose-500" />
        </div>
        <div className="flex-1">
          <DialogTitle className="text-lg font-semibold text-neutral-800 mb-2">
            Delete {selectedCount === 1 ? "Trip" : `${selectedCount} Trips`}
          </DialogTitle>
          <DialogDescription className="text-neutral-600 text-sm mb-4 font-light">
            Are you sure you want to delete {selectedCount} selected{" "}
            {selectedCount === 1 ? "trip" : "trips"}?
          </DialogDescription>
        </div>
      </DialogHeader>
      <DialogFooter className="gap-3 sm:gap-1">
        <CancelButton
          onClick={onClose}
          disabled={isDeleting}
          className="hover:bg-neutral-100"
        />
        <ConfirmButton
          title={`Delete ${selectedCount === 1 ? "Trip" : "Trips"}`}
          onClick={onConfirm}
          disabled={isDeleting}
          isLoading={isDeleting}
          loadingText={null}
          type="destructive"
          className="shadow-sm"
        />
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

const ActiveTrips = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuthStore();
  const userId = currentUser?.user_id;
  const [activeFilter, setActiveFilter] = useState("all");
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedTrips, setSelectedTrips] = useState<string[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: trips = [], isLoading } = useQuery({
    queryKey: ["trips", userId],
    queryFn: () => tripService.getTrips(),
    enabled: !!userId,
  });

  const deleteMutation = useMutation({
    mutationFn: async (tripIds: string[]) => {
      await Promise.all(tripIds.map((id) => tripService.deleteTrip(id)));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trips"] });
      setShowDeleteModal(false);
      setIsSelectionMode(false);
      setSelectedTrips([]);
      toast({
        title: "Trips deleted",
        description: `Successfully deleted ${selectedTrips.length} trip${
          selectedTrips.length !== 1 ? "s" : ""
        }`,
      });
    },
  });

  const handleSelectTrip = (tripId: string) => {
    setSelectedTrips((prev: string[]) =>
      prev.includes(tripId)
        ? prev.filter((id) => id !== tripId)
        : [...prev, tripId]
    );
  };

  const handleDeleteSelected = () => {
    if (selectedTrips.length > 0) {
      setShowDeleteModal(true);
    }
  };

  const exitSelectionMode = () => {
    setIsSelectionMode(false);
    setSelectedTrips([]);
  };

  const filteredTrips = useMemo(() => {
    return trips.filter((trip) => {
      if (activeFilter === "all") return true;
      if (activeFilter === "my") return trip.is_owned;
      if (activeFilter === "shared") return !trip.is_owned;
      return false;
    });
  }, [trips, activeFilter]);

  const handleCreateTrip = () => {
    navigate("/new-trip");
  };

  const handleExplore = () => {
    navigate("/explore");
  };

  const LoadingSkeleton = () => (
    <div className="w-full">
      <div className="flex justify-between items-center mb-4 pt-1">
        <div className="h-10 w-32 bg-slate-200 animate-pulse rounded-lg"></div>
        <div className="flex items-center gap-3">
          <div className="h-10 w-32 bg-slate-200 animate-pulse rounded-full"></div>
          <div className="h-10 w-40 bg-slate-200 animate-pulse rounded-full"></div>
        </div>
      </div>

      <div className="mb-6">
        <div className="h-10 w-full max-w-md bg-slate-200 animate-pulse rounded-lg"></div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {[...Array(8)].map((_, index) => (
          <div
            key={index}
            className="w-full rounded-xl overflow-hidden border border-slate-200"
          >
            <div className="h-48 bg-slate-200 animate-pulse"></div>
            <div className="p-3 space-y-3">
              <div className="h-5 w-3/4 bg-slate-200 animate-pulse rounded"></div>
              <div className="flex justify-between items-center">
                <div className="h-4 w-1/2 bg-slate-200 animate-pulse rounded"></div>
                <div className="flex -space-x-2">
                  {[...Array(3)].map((_, i) => (
                    <div
                      key={i}
                      className="w-6 h-6 rounded-full bg-slate-200 animate-pulse"
                    ></div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  if (isLoading) return <LoadingSkeleton />;
  if (!trips.length)
    return (
      <div className="w-full relative min-h-screen pb-24">
        <PageHeaderBar title="Trips" />
        <div className="flex flex-col items-center justify-center h-full">
          <div className="bg-neutral-50 rounded-full p-4 mb-4">
            <Compass className="w-8 h-8 text-neutral-500" strokeWidth={1.5} />
          </div>
          <h3 className="text-lg font-medium text-neutral-900 mb-2">
            No trips found
          </h3>
          <p className="text-neutral-500 text-center text-sm max-w-xs font-light">
            Create a trip to get started
          </p>
          <div className="mt-8 justify-center w-full flex flex-row items-center gap-3">
            <button
              onClick={handleCreateTrip}
              className="text-sm py-2 px-6 justify-center h-full rounded-full bg-indigo-700 hover:bg-indigo-800 transition-all duration-300 text-white flex items-center gap-2"
            >
              <PlusCircleIcon size={20} strokeWidth={1.5} />
              <span className="font-light">Create</span>
            </button>
            <button
              onClick={handleExplore}
              className="text-sm py-2 px-6 justify-center h-full rounded-full bg-white hover:bg-indigo-50 transition-all duration-300 text-indigo-700 flex items-center gap-2 border-2 border-indigo-700"
            >
              <Search size={20} strokeWidth={2} />
              <span className="font-light">Explore</span>
            </button>
          </div>
        </div>
      </div>
    );

  return (
    <div className="w-full relative min-h-screen pb-24">
      <PageHeaderBar title="Trips" />
      <TripFilters
        activeFilter={activeFilter as "all" | "my" | "shared"}
        onChange={setActiveFilter}
        isSelectionMode={isSelectionMode}
        selectedTrips={selectedTrips}
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 2xl:gap-1">
        {filteredTrips.map((trip) => (
          <TripCard
            key={trip.id}
            trip={trip}
            isSelectionMode={isSelectionMode}
            selectedTrips={selectedTrips}
            onSelectTrip={handleSelectTrip}
          />
        ))}
      </div>
      <div className="fixed bottom-12 left-0 right-0 flex justify-center">
        <div className="relative">
          {/* Initial Select Button */}
          <div
            className={`transform transition-all duration-300 ease-in-out ${
              isSelectionMode
                ? "opacity-0 scale-95 pointer-events-none"
                : "opacity-100 scale-100"
            }`}
          >
            <button
              onClick={() => setIsSelectionMode(true)}
              className="px-6 py-3 text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 rounded-full transition-all duration-300 flex items-center gap-2 shadow-sm"
            >
              <CheckIcon className="w-5 h-5" />
              Select Trips
            </button>
          </div>

          {/* Selection Mode Controls */}
          <div
            className={`absolute top-0 left-1/2 -translate-x-1/2 flex items-center gap-3 transform transition-all duration-300 ease-in-out ${
              isSelectionMode
                ? "opacity-100 scale-100 z-20"
                : "opacity-0 scale-95 pointer-events-none"
            }`}
          >
            <button
              onClick={exitSelectionMode}
              className="h-12 px-6 text-sm font-medium bg-white text-neutral-600 hover:text-neutral-800 hover:bg-neutral-50 rounded-full transition-all duration-300 border border-neutral-200"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteSelected}
              disabled={selectedTrips.length === 0}
              className={`h-12 px-6 text-sm font-medium rounded-full transition-all duration-300 inline-flex items-center whitespace-nowrap ${
                selectedTrips.length === 0
                  ? "bg-neutral-100 text-neutral-400"
                  : "bg-rose-50 ring-1 ring-rose-200 text-rose-600 hover:bg-rose-100"
              }`}
            >
              {selectedTrips.length > 0 && (
                <span className="inline-flex items-center justify-center bg-rose-100 ring-1 ring-rose-200 text-rose-600 rounded-full w-5 h-5 text-xs font-medium mr-2">
                  {selectedTrips.length}
                </span>
              )}
              Delete Selected
            </button>
          </div>
        </div>
      </div>
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={() => deleteMutation.mutate(selectedTrips)}
        selectedCount={selectedTrips.length}
        isDeleting={deleteMutation.isPending}
      />
    </div>
  );
};

export default ActiveTrips;
